import { useState } from "react";
import {
  Layers,
  Lock,
  Mail,
  Eye,
  EyeOff,
  Loader2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { apiv2 } from "../apiv2";
import { setUser } from "../auth";
import GreenTendrils from "./GreenTendrils";

interface Props {
  onLoginSuccess: () => void;
}

export default function LoginPage({ onLoginSuccess }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const user = await apiv2.login(email.trim(), password);
      setUser({
        id: user.id,
        email: user.email,
        full_name: user.full_name,
        role: user.role,
        permissions: user.permissions ?? [],
        is_active: user.is_active,
        must_change_password: user.must_change_password,
      });
      onLoginSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-500 relative overflow-hidden">
      {/* Background effects */}
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <div className="glow-orb glow-orb-3" />
      <GreenTendrils />

      {/* Header */}
      <header
        className="relative z-10 flex items-center h-12 px-6 border-b border-white/[0.04]"
        style={{
          background: "rgba(14,14,24,0.8)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div className="flex items-center gap-2 text-brand-500">
          <Layers size={18} />
          <span className="text-sm font-black tracking-wide">
            ZTD<span className="text-white/30 font-normal">.si</span>
          </span>
        </div>
      </header>

      {/* Hero section — mirrors the landing page layout */}
      <section className="relative min-h-[calc(100vh-3rem)] flex items-center">
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 w-full flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left: Title & tagline */}
          <div className="flex-1 space-y-8 animate-fade-in">
            <div
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-sm font-medium"
              style={{ background: "rgba(134,188,37,0.08)" }}
            >
              <Sparkles size={14} />
              SI-Powered Data Agents
            </div>

            <h1 className="text-6xl md:text-7xl font-black leading-[0.95] tracking-tight">
              <span className="gradient-text-green">ZeroTouchData</span>
              <span className="text-white/40 font-extralight">.si</span>
            </h1>

            <p className="text-xl md:text-2xl text-white/70 font-light leading-relaxed max-w-lg">
              Your data pipeline, fully automated.
              <br />
              <span className="text-white font-medium">LLM-powered agents</span>
              , human-supervised results.
            </p>
          </div>

          {/* Right: Login card (replaces the feature hero card) */}
          <div className="flex-shrink-0 w-full max-w-md animate-slide-up">
            <div className="glass-hero-card rounded-2xl p-8 space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-white">Welcome back</h2>
                <p className="text-sm text-white/40 mt-1">
                  Sign in to your workspace
                </p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300">
                  <AlertTriangle size={16} className="flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="block text-xs font-medium text-white/50 mb-1.5">
                    Email
                  </label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20"
                    />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full pl-10 pr-4 py-3 bg-surface-300/50 border border-white/8 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 transition"
                      autoComplete="email"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-medium text-white/50 mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-white/20"
                    />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full pl-10 pr-12 py-3 bg-surface-300/50 border border-white/8 rounded-xl text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/30 transition"
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/20 hover:text-white/50 transition"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !email.trim() || !password.trim()}
                  className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                    boxShadow:
                      "0 0 30px rgba(134,188,37,0.3), 0 4px 20px rgba(0,0,0,0.3)",
                  }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
